import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/products', label: 'Products' },
    { path: '/infrastructure', label: 'Infrastructure' },
    { path: '/contact', label: 'Contact Us' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-navy-900/95 backdrop-blur-md shadow-lg shadow-black/20'
          : 'bg-navy-900'
      }`}
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex justify-between items-center h-16 md:h-18">

          {/* Logo + Wordmark */}
          <Link to="/" className="flex items-center gap-3 shrink-0 group" aria-label="Mato Industries - Home">
              <img
                src="/mato_logo_black.svg"
                alt="Mato Industries Logo"
                className="h-12 w-auto brightness-0 invert"
                width="48"
                height="48"
              />
            <div>
              <div className="text-white font-bold text-xl leading-tight tracking-tight">
                Mato Industries
              </div>
              <div className="text-slate-400 text-xs font-semibold tracking-widest uppercase">
                Est. 1968
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                  isActive(item.path)
                    ? 'text-white bg-white/10'
                    : 'text-slate-300 hover:text-white hover:bg-white/8'
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-brand-500 rounded-full" />
                )}
              </Link>
            ))}
            <a
              href="tel:+919910934449"
              className="ml-3 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer"
            >
              +91 99109 34449
            </a>
          </nav>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors duration-200 cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <nav
          id="mobile-menu"
          className="md:hidden border-t border-white/10 bg-navy-900"
          aria-label="Mobile navigation"
        >
          <ul className="flex flex-col px-4 py-3 gap-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer ${
                    isActive(item.path)
                      ? 'text-white bg-white/10 border-l-2 border-brand-500 pl-3'
                      : 'text-slate-300 hover:text-white hover:bg-white/8'
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="pt-2 pb-1">
              <a
                href="tel:+919910934449"
                className="flex items-center justify-center px-4 py-3 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer"
              >
                Call: +91 99109 34449
              </a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Header;
